import type { FieldSeason } from '@protector/entities/property/models';
import type { Field, __legacy__SeasonField } from '@protector/entities/region/models';
import type { SeasonField } from '@protector/entities/season-field/models';
import type { Nullable, UUID, V4Page } from '@protector/utils/types';
import axios from 'axios-observable';
import { CHUNK_PAGE, CORE_SERVICES_API_URL } from 'config/constants';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { getObservables } from '../../core/rxjs-utils';

export interface FieldsByIdsUrlParams {
  length: number;
  geometryDate?: Nullable<string>;
  includeGeometry?: boolean;
  includeArchived?: boolean;
}

export const fieldsByIdsUrl = ({
  length,
  geometryDate = null,
  includeGeometry = true,
  includeArchived = true
}: FieldsByIdsUrlParams): string => {
  const baseUrl = `${CORE_SERVICES_API_URL}/v2/fields/ids`;
  const params = new URLSearchParams({
    page: '0',
    include_extended: 'true',
    size: length.toString()
  });

  if (includeGeometry) {
    params.append('attributes', 'geometry');
  }

  if (geometryDate) {
    params.append('reference_date', geometryDate);
  }

  if (includeArchived) {
    params.append('include_archived', includeArchived.toString());
  }

  return `${baseUrl}?${params.toString()}`;
};

const buildFieldByIdsRequest = (ids: UUID[]) => ({ field_ids: ids });

export const getFieldsByIds = (ids: UUID[], geometryDate: Nullable<string> = null, includeGeometry = true, includeArchived = false) => {
  return axios
    .post<V4Page<Field>>(
      fieldsByIdsUrl({ length: ids.length, geometryDate, includeGeometry, includeArchived }),
      buildFieldByIdsRequest(ids),
      { headers: { 'client-id': 'strix-ui' } }
    )
    .pipe(concatMap(r => of(r.data)));
};

export type FieldIdsByEndDateInSeasonField = Record<string, string[]>;

export const getAllFieldsByIds = (
  ids: UUID[],
  useAsync = true,
  geometryDate: Nullable<string> = null,
  includeGeometry = true,
  fieldIdsByEndDateInSeasonField?: FieldIdsByEndDateInSeasonField,
  includeArchived = false
): Observable<Field[][]> => {
  let observables: Observable<Field[]>[] = [];

  const endDates = Object.keys(fieldIdsByEndDateInSeasonField ?? {});
  const splitRequestByEndDate = fieldIdsByEndDateInSeasonField && endDates.length;
  if (splitRequestByEndDate) {
    observables = endDates
      .map(endDate => {
        const fieldIds = fieldIdsByEndDateInSeasonField[endDate];
        return getFieldsByIds(fieldIds, endDate, includeGeometry, includeArchived).pipe(concatMap(p => of(p.content)));
      })
      .flat();
  }

  if (includeGeometry && !splitRequestByEndDate) {
    const totalChunkedPages = Math.floor(ids.length / CHUNK_PAGE + 1);
    const pages = [...Array(totalChunkedPages).keys()];

    observables = pages.map(page =>
      getFieldsByIds(ids.slice(page * CHUNK_PAGE, (page + 1) * CHUNK_PAGE), geometryDate, includeGeometry, includeArchived).pipe(
        concatMap(p => of(p.content))
      )
    );
  }

  if (!includeGeometry && !splitRequestByEndDate) {
    observables = [getFieldsByIds(ids, geometryDate, includeGeometry, includeArchived).pipe(concatMap(p => of(p.content)))];
  }

  return getObservables(observables, useAsync);
};

export const mapSeasonFieldToFieldSeason = (seasonField: __legacy__SeasonField): FieldSeason => {
  return {
    id: seasonField.id,
    areaId: seasonField.id,
    varieties: seasonField.varieties_ids,
    seasonId: seasonField.season_id
  };
};

export const mapSeasonFieldToFieldSeasonByFieldId = (seasonField: SeasonField): FieldSeason => {
  return {
    id: seasonField.id,
    areaId: seasonField.field_id,
    seasonId: seasonField.season_id as UUID,
    varieties: (seasonField.varieties_ids ?? []) as UUID[],
    endsAt: seasonField.end_date
  };
};
